import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldOff, AlertTriangle, Users, ListFilter } from 'lucide-react';

const STATUS_STYLE = {
  blocked: { color: '#E24B4A', bg: 'rgba(226,75,74,0.1)', label: 'BLOCKED' },
  referred: { color: '#E8A020', bg: 'rgba(232,160,32,0.1)', label: 'REFERRED' },
};

export default function AppetiteAnalyticsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/appetite').then(r => setData(r.data))
      .catch(err => console.error('API error:', err?.message)).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  if (!data) return <p className="text-sm" style={{ color: '#5C6B7A' }}>Unable to load appetite analytics.</p>;

  const maxTotal = Math.max(1, ...data.by_class.map(c => c.total));
  const empty = data.totals.blocked + data.totals.referred === 0;

  return (
    <div className="space-y-5" data-testid="appetite-analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs" style={{ color: '#5C6B7A' }}>
          Read-only analytics — where quotes hit the appetite boundary and who pushes it.
        </p>
        <a href="/rates?tab=appetite" data-testid="edit-appetite-rules-link"
          className="flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: '#1A4C7C' }}>
          Edit appetite rules →
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="p-4 rounded-md relative overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-total-blocked">
          <div className="absolute top-0 left-0 w-1 h-full" style={{ background: '#E24B4A' }} />
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#5C6B7A' }}>Blocked (out of appetite)</p>
            <ShieldOff className="w-4 h-4" style={{ color: '#E24B4A' }} />
          </div>
          <p className="text-2xl font-bold font-mono mt-1.5" style={{ color: '#0D1B2A' }}>{data.totals.blocked}</p>
        </Card>
        <Card className="p-4 rounded-md relative overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-total-referred">
          <div className="absolute top-0 left-0 w-1 h-full" style={{ background: '#E8A020' }} />
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#5C6B7A' }}>Forced referrals</p>
            <AlertTriangle className="w-4 h-4" style={{ color: '#E8A020' }} />
          </div>
          <p className="text-2xl font-bold font-mono mt-1.5" style={{ color: '#0D1B2A' }}>{data.totals.referred}</p>
        </Card>
        <Card className="p-4 rounded-md relative overflow-hidden col-span-2 md:col-span-1" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-classes-affected">
          <div className="absolute top-0 left-0 w-1 h-full" style={{ background: '#1A4C7C' }} />
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#5C6B7A' }}>Classes hit</p>
            <ListFilter className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          </div>
          <p className="text-2xl font-bold font-mono mt-1.5" style={{ color: '#0D1B2A' }}>{data.by_class.length}</p>
        </Card>
      </div>

      {empty ? (
        <Card className="p-8 text-center rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-empty-state">
          <p className="text-sm font-medium" style={{ color: '#0D1B2A' }}>No out-of-appetite activity yet</p>
          <p className="text-xs mt-1" style={{ color: '#5C6B7A' }}>Blocked and forced-referral quotes will appear here as brokers submit business.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-by-class">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Pressure by class</h3>
              <div className="space-y-2.5">
                {data.by_class.map(c => (
                  <div key={c.class_id} data-testid={`appetite-class-${c.class_id}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: '#0D1B2A' }}>{c.class_name}</span>
                      <span className="font-mono" style={{ color: '#5C6B7A' }}>
                        <span style={{ color: '#E24B4A' }}>{c.blocked} blocked</span> · <span style={{ color: '#B27516' }}>{c.referred} referred</span>
                      </span>
                    </div>
                    <div className="h-2 rounded-full flex overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
                      <div style={{ width: `${(c.blocked / maxTotal) * 100}%`, background: '#E24B4A' }} />
                      <div style={{ width: `${(c.referred / maxTotal) * 100}%`, background: '#E8A020' }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="space-y-4">
              <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-top-reasons">
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Top reasons</h3>
                <div className="space-y-2">
                  {data.top_reasons.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="font-mono font-bold shrink-0 w-6" style={{ color: STATUS_STYLE[r.status]?.color }}>{r.count}×</span>
                      <span className="leading-snug" style={{ color: '#3A4A5A' }}>
                        <span className="font-semibold" style={{ color: '#0D1B2A' }}>{r.class_name}:</span> {r.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-top-users">
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: '#0D1B2A' }}>
                  <Users className="w-3.5 h-3.5" /> Who pushes outside appetite
                </h3>
                <div className="space-y-1.5">
                  {data.top_users.map(u => (
                    <div key={u.user_email} className="flex items-center justify-between text-xs">
                      <span style={{ color: '#0D1B2A' }}>{u.user_email}</span>
                      <span className="font-mono" style={{ color: '#5C6B7A' }}>{u.count} attempts · {u.blocked} blocked</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>

          <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="appetite-recent-events">
            <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Recent events</h3>
            </div>
            <div className="divide-y divide-[rgba(136,150,168,0.08)]">
              {data.recent.map(e => {
                const st = STATUS_STYLE[e.status] || STATUS_STYLE.referred;
                return (
                  <div key={e.event_id} className="px-4 py-2.5 flex items-start gap-3" data-testid={`appetite-event-${e.event_id}`}>
                    <span className="text-[9px] px-2 py-0.5 rounded-full font-bold shrink-0 mt-0.5" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs" style={{ color: '#0D1B2A' }}>
                        <span className="font-semibold">{e.entity_name}</span> · {e.class_id?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-[11px] leading-snug mt-0.5" style={{ color: '#5C6B7A' }}>{e.reason}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] font-mono" style={{ color: '#64748B' }}>
                        {e.created_at ? new Date(e.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                      <p className="text-[10px]" style={{ color: '#64748B' }}>{e.user_email}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
